import { InlineValue, ContentLink, useContentSingletons, usePageContent, useXcoreQuery } from "@robe/client/xcore-ui";
import { Box, Button, Collapse, Container, Flex, Link as UILink, Stack, Typography } from "@xcorejs/ui";
import DownloadIcon4 from "components/Icons/download/dl-datasheet.svg";
import DownloadCard from "design-system/robe/Cards/ProductDownload";
import RobeProductDetailHeading from "design-system/robe/RobeProductDetailHeading";
import { useEffect, useMemo, useState } from "react";
import { useLayout } from "xcore";
import { Product, ProductDetailPage } from "xcore/types";
import { useSection } from "../data";
import { ProductDownloadsModal } from "./ProductDownloadsModal";
import { useRouter } from "next/router";
import { ProductDownloadsResponse } from "@robe/core";
import { usePdfLambda } from "hooks";
import { resolveSFCategoryLabel } from "@robe/client";

const ProductDownloads = () => {
  const { locale } = useRouter();
  const {
    header: { nav },
    stringify,
  } = useLayout();
  const { id: productId } = usePageContent<Product>();
  const [{ values }] = useContentSingletons<[ProductDetailPage]>();

  const anchor = "#" + stringify(values.anchorDownloads);

  const [active, toggle] = useSection(anchor);

  const { data } = useXcoreQuery((core) => core.web.getProductDownloads({ productId: `${productId}` })); // This call needs to be done on client side. Otherwise group-specific files for logged user are not fetched
  const categoryDetail = useModalCategory(data);

  const categories = data?.categories ?? [];
  const downloads = data?.downloads ?? [];

  const datasheetUrl = usePdfLambda({ id: productId, locale, type: "product" });

  return (
    <Box id={anchor}>
      <Box id={stringify(values.anchorDownloads)} transform={{ _: "translateY(-6rem)", md: "translateY(-13.9rem)" }} />
      {data ? (
        <Box>
          {categories?.map((c) => (
            <Box key={c.id} id={`downloads-${c.id}`} transform={{ _: "translateY(-6rem)", md: "translateY(-13.9rem)" }} />
          ))}

          {categoryDetail && (
            <ProductDownloadsModal
              id={productId}
              category={categoryDetail}
              downloads={downloads}
              buttonSuppFiles={stringify(values.displaySuppFiles)!}
            />
          )}

          <Container flexDirection="column" py={{ _: "1.5rem", sm: "5rem" }}>
            <RobeProductDetailHeading onClick={toggle} active={active} {...(active && { mb: { _: "3rem", sm: "4rem" } })}>
              <InlineValue value={values.downloads} />
            </RobeProductDetailHeading>

            <Collapse open={active} mt={{ _: "-1rem", sm: 0 }}>
              <Stack direction="column" gap={{ _: "2rem", sm: "4rem" }}>
                <Typography variant="lead" textAlign="center" mt={{ _: "-1rem", sm: 0 }}>
                  <InlineValue value={values.downloadIntro} />
                </Typography>
                <Flex
                  justifyContent="center"
                  flexWrap="wrap"
                  margin={{
                    _: "0 0 -1rem 0",
                    xs: "0 0 -2.2rem 0",
                  }}
                >
                  {datasheetUrl && (
                    <Flex
                      maxWidth={{ _: "100%", xs: "50%", sm: "25%" }}
                      flex={{ _: "0 0 100%", xs: "0 0 50%", sm: "0 0 25%" }}
                      paddingX={{ _: 0, sm: "1.5rem" }}
                      paddingBottom={{ _: "1rem", xs: "2.2rem" }}
                    >
                      <UILink variant="simple" target="_blank" href={datasheetUrl} width="100%">
                        <DownloadCard title={stringify(values.dataSheetMenu)} icon={<DownloadIcon4 />} />
                      </UILink>
                    </Flex>
                  )}

                  {categories.map((category) => {
                    const iconUrl = category.fileId ? (category.imageInfos.find((info) => info.id === category.fileId)?.url ?? null) : null;
                    return (
                      <Flex
                        key={category.id}
                        maxWidth={{ _: "100%", xs: "50%", sm: "25%" }}
                        flex={{ _: "0 0 100%", xs: "0 0 50%", sm: "0 0 25%" }}
                        paddingX={{ _: 0, sm: "1.5rem" }}
                        paddingBottom={{ _: "1rem", xs: "2.2rem" }}
                      >
                        <UILink variant="simple" href={`#downloads-${category.id}`} width="100%">
                          <DownloadCard title={resolveSFCategoryLabel({ category, stringifyFunc: stringify })} icon={iconUrl} fillWhite />
                        </UILink>
                      </Flex>
                    );
                  })}
                </Flex>
                {categories.length > 0 && (
                  <Flex justifyContent="center">
                    <ContentLink content={nav.support} queries={{ product: productId }}>
                      <Button>{stringify(values.displaySuppFiles)}</Button>
                    </ContentLink>
                  </Flex>
                )}
              </Stack>
            </Collapse>
          </Container>
        </Box>
      ) : null}
    </Box>
  );
};

export default ProductDownloads;

const useModalCategory = (data: ProductDownloadsResponse | undefined) => {
  const [hash, setHash] = useState<string>();

  const categories = data?.categories;

  useEffect(() => {
    setHash(window.location.hash);

    const handleSetHash = () => setHash(window.location.hash);

    window.addEventListener("hashchange", handleSetHash);

    return () => window.removeEventListener("hashchange", handleSetHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const category = useMemo(() => {
    const slug = hash?.startsWith("#downloads-") && Number(hash.slice(11));
    return categories?.find((c) => c.id === slug);
  }, [categories, hash]);

  return category;
};
